import { AreaButton, Banner, BodyText, Caption, Icon, SectionTitle } from '@if/mui'
import { Box, Card, CardContent, Stack } from '@mui/material'
import { useMerchant } from '@src/containers/App/MerchantProvider'
import { useBorrowerContext } from '@src/hooks/borrower-context-hooks'
import { CustomBuydownTimeline } from '@src/types/CustomBuydown'
import { Info, Lock, Plus } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { ECustomBuydownPlanStatus } from '../planStatus'
import { useCreatePlanDialog } from './CreatePlanDialog'
import { PlanCard } from './PlanCard'

const CUSTOM_BUYDOWN_OWNED_FIELD = 'CustomBuydown'

export interface PlanTimelineProps {
  timeline: CustomBuydownTimeline
  merchantId: string
}

export const PlanTimeline = ({ timeline, merchantId }: PlanTimelineProps) => {
  const { t } = useTranslation()
  const merchant = useMerchant()
  const context = useBorrowerContext()

  const isLocked = !!merchant.parentId && (merchant.bannerOwnedFields ?? []).includes(CUSTOM_BUYDOWN_OWNED_FIELD)
  const dialog = useCreatePlanDialog(merchantId, timeline)

  const { active, scheduled } = timeline

  return (
    <Stack spacing={3}>
      {isLocked && (
        <Banner severity="info" icon={Lock}>
          {t('customBuydown.inherited.lockedNotice')}
        </Banner>
      )}

      <Stack spacing={2}>
        <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" useFlexGap>
          <SectionTitle>{t('customBuydown.timeline.sectionTitle')}</SectionTitle>
          <Stack direction="row" spacing={0.75} alignItems="center">
            <Icon icon={Info} size="sm" color="action" />
            <Caption>{isLocked ? t('customBuydown.timeline.hintLocked') : t('customBuydown.timeline.hint')}</Caption>
          </Stack>
        </Stack>

        {!active && !scheduled && isLocked ? (
          <Card>
            <CardContent sx={{ py: 6, textAlign: 'center' }}>
              <BodyText>{t('customBuydown.timeline.empty', { context })}</BodyText>
            </CardContent>
          </Card>
        ) : (
          <Box
            sx={{
              display: 'grid',
              gap: 3,
              gridTemplateColumns: { xs: '1fr', lg: isLocked && (!active || !scheduled) ? '1fr' : '1fr 1fr' },
              alignItems: 'stretch',
            }}
          >
            {!active && !scheduled ? (
              <AreaButton
                onClick={() => void dialog.create()}
                icon={<Icon icon={Plus} size="lg" />}
                title={t('customBuydown.timeline.createActiveTitle')}
                description={t('customBuydown.timeline.createActiveDescription')}
              />
            ) : (
              <>
                {active ? (
                  <PlanCard plan={active} status={ECustomBuydownPlanStatus.Active} locked={isLocked} siblingPlan={scheduled} />
                ) : (
                  !isLocked && (
                    <AreaButton
                      onClick={() => void dialog.create()}
                      icon={<Icon icon={Plus} size="lg" />}
                      title={t('customBuydown.timeline.createActiveTitle')}
                      description={t('customBuydown.timeline.createGapDescription')}
                    />
                  )
                )}
                {scheduled ? (
                  <PlanCard plan={scheduled} status={ECustomBuydownPlanStatus.Scheduled} locked={isLocked} siblingPlan={active} />
                ) : (
                  !isLocked && (
                    <AreaButton
                      onClick={() => void dialog.create(active)}
                      icon={<Icon icon={Plus} size="lg" />}
                      title={t('customBuydown.timeline.createScheduledTitle')}
                      description={t('customBuydown.timeline.createScheduledDescription')}
                    />
                  )
                )}
              </>
            )}
          </Box>
        )}
      </Stack>
    </Stack>
  )
}
